import React, { useState, useEffect } from 'react';
import {
  Segmented,
  Tooltip,
  Flex,
  Space,
  Divider,
  Avatar,
  Typography,
  Button,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../helpers/use-store';
import {
  SocialProvider,
  PublishedAccount,
  tiktokDefaultMetadata,
  youtubeDefaultMetadata,
  instagramDefaultMetadata,
  facebookDefaultMetadata,
  TiktokMetadata,
  YouTubeMetadata,
  InstagramMetadata,
  FacebookMetadata,
} from '@monorepo/types';
import SocialIcon from '../../../../components/social-provider-icon/social-provider-icon';
import SocialAccountSelect from '../../../social-accounts/social-account';
import YoutubeForm from './youtube-form/youtube-form';
import TiktokForm from './tiktok-form/tiktok-form';
import TiktokAgreement from './tiktok-form/tiktok-agreement';
import InstagramForm from './instagram-form/instagram-form';
import FacebookForm from './facebook-form/facebook-form';

export interface SocialAccountTabsProps {
  activeTab: SocialProvider | 'add';
  platformsMap: Map<SocialProvider, PublishedAccount>;
  setActiveTab: (activeTab: SocialProvider | 'add') => void;
  publishedAccounts: PublishedAccount[];
  onPublishedAccountUpdate?: (publishedAccount: PublishedAccount) => void;
  onPublishedAccountRemove?: (publishedAccount: PublishedAccount) => void;
}

const SocialAccountTabs = ({
  publishedAccounts = [],
  onPublishedAccountUpdate,
  onPublishedAccountRemove,
  activeTab,
  setActiveTab,
  platformsMap,
}: SocialAccountTabsProps) => {
  const { t } = useTranslation();
  const {
    dataStore: { socialAccountStore },
  } = useStore();

  const handleTabChange = (newTab: any) => {
    setActiveTab(newTab);
  };

  // Handle account selection from SocialAccountSelect
  const handleAccountSelect = (accountId: string) => {
    if (!accountId) return;

    const selectedAccount = socialAccountStore.get(accountId);
    if (!selectedAccount) return;

    const platform = selectedAccount.provider;
    const metadata = selectedAccount.metadata;

    // Create a new published account
    const publishedAccount: PublishedAccount = {
      socialAccountId: accountId,
      socialPlatform: platform,
      metadata: metadata,
    };

    // Call the update handler
    onPublishedAccountUpdate?.(publishedAccount);
    setActiveTab(platform);
  };

  // Handle account removal
  const handleRemoveAccount = (platform: SocialProvider) => {
    const publishedAccount = platformsMap.get(platform);
    if (publishedAccount) {
      onPublishedAccountRemove?.(publishedAccount);

      // Update active tab if the removed platform is the active one
      if (platform === activeTab) {
        const remainingPlatforms = new Map(platformsMap);
        remainingPlatforms.delete(platform);

        if (remainingPlatforms.size > 0) {
          setActiveTab(Array.from(remainingPlatforms.keys())[0]);
        } else {
          setActiveTab('add');
        }
      }
    }
  };

  // Get the display name for a platform
  const getPlatformDisplayName = (platform: SocialProvider) => {
    switch (platform) {
      case SocialProvider.TikTok:
        return 'TikTok';
      case SocialProvider.YouTube:
        return 'YouTube';
      case SocialProvider.Instagram:
        return 'Instagram';
      case SocialProvider.Facebook:
        return 'Facebook';
      default:
        return platform;
    }
  };

  // Get the active published account
  const getActivePublishedAccount = () => {
    if (activeTab === 'add') return null;
    return platformsMap.get(activeTab) || null;
  };

  const activePublishedAccount = getActivePublishedAccount();
  const selectedAccount = activePublishedAccount
    ? socialAccountStore.get(activePublishedAccount.socialAccountId)
    : null;

  // Handle metadata updates
  const handleMetadataUpdate = (
    updates: Partial<
      TiktokMetadata | YouTubeMetadata | InstagramMetadata | FacebookMetadata
    >
  ) => {
    if (!activePublishedAccount) return;

    // Create updated published account with new metadata
    const updatedAccount: PublishedAccount = {
      ...activePublishedAccount,
      metadata: {
        ...activePublishedAccount.metadata,
        ...updates,
      },
    };

    onPublishedAccountUpdate?.(updatedAccount);
  };

  // Get additional form for the selected social provider
  const getSocialProviderAdditionalInfo = () => {
    if (!selectedAccount || !activePublishedAccount) {
      return null;
    }

    const provider = selectedAccount.provider;

    if (provider === SocialProvider.TikTok) {
      return (
        <>
          <TiktokForm
            onUpdate={handleMetadataUpdate}
            metadata={
              (activePublishedAccount.metadata as TiktokMetadata) ||
              (selectedAccount.metadata as TiktokMetadata) ||
              tiktokDefaultMetadata
            }
          />
          <Divider style={{ margin: '15px 0' }} />
        </>
      );
    }

    if (provider === SocialProvider.YouTube) {
      return (
        <>
          <YoutubeForm
            onUpdate={handleMetadataUpdate}
            metadata={
              (activePublishedAccount.metadata as YouTubeMetadata) ||
              (selectedAccount.metadata as YouTubeMetadata) ||
              youtubeDefaultMetadata
            }
          />
          <Divider style={{ margin: '15px 0' }} />
        </>
      );
    }

    if (provider === SocialProvider.Instagram) {
      return (
        <>
          <InstagramForm
            onUpdate={handleMetadataUpdate}
            metadata={
              (activePublishedAccount.metadata as InstagramMetadata) ||
              (selectedAccount.metadata as InstagramMetadata) ||
              instagramDefaultMetadata
            }
          />
          <Divider style={{ margin: '15px 0' }} />
        </>
      );
    }

    if (provider === SocialProvider.Facebook) {
      return (
        <>
          <FacebookForm
            onUpdate={handleMetadataUpdate}
            metadata={
              (activePublishedAccount.metadata as FacebookMetadata) ||
              (selectedAccount.metadata as FacebookMetadata) ||
              facebookDefaultMetadata
            }
          />
          <Divider style={{ margin: '15px 0' }} />
        </>
      );
    }

    return null;
  };

  // Get social agreement component based on active platform
  const getSocialAgreement = () => {
    if (!selectedAccount || !activePublishedAccount) {
      return null;
    }

    const provider = selectedAccount.provider;

    if (provider === SocialProvider.TikTok) {
      return (
        <TiktokAgreement
          metadata={
            (activePublishedAccount.metadata as TiktokMetadata) ||
            (selectedAccount.metadata as TiktokMetadata) ||
            tiktokDefaultMetadata
          }
        />
      );
    }

    return null;
  };

  // Get options for the segmented control
  const getOptions = () => {
    const options = Array.from(platformsMap.keys()).map((platform) => ({
      label: (
        <Flex align="center" justify={'space-between'} gap={15}>
          <Flex align={'center'} gap={10}>
            <SocialIcon provider={platform} />
            <span>{getPlatformDisplayName(platform)}</span>
          </Flex>
          <Button
            size={'small'}
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveAccount(platform);
            }}
            type={'text'}
            icon={<CloseOutlined />}
          ></Button>
        </Flex>
      ),
      value: platform,
    }));

    // Add the "+" button as the last option
    options.push({
      label: (
        <Tooltip title={t('videoEditor.socialPublish.addPlatform')}>
          <PlusOutlined />
        </Tooltip>
      ),
      // @ts-expect-error value type
      value: 'add',
    });

    return options;
  };

  const isMetadataValid = () => {
    if (!activePublishedAccount) {
      return {
        valid: false,
        message: t('videoEditor.socialPublish.validation.noAccount'),
      };
    }

    const provider = activePublishedAccount.socialPlatform;

    if (provider === SocialProvider.TikTok) {
      const tiktokMetadata = (activePublishedAccount.metadata ||
        {}) as TiktokMetadata;

      if (!tiktokMetadata.privacy) {
        return {
          valid: false,
          message: t('videoEditor.socialPublish.validation.tiktokPrivacy'),
        };
      }

      // Check if disclosure is enabled but no brand options selected
      if (
        tiktokMetadata.isDisclosureEnabled &&
        !tiktokMetadata.brandOptions.yourBrand &&
        !tiktokMetadata.brandOptions.brandedContent
      ) {
        return {
          valid: false,
          message: t('videoEditor.socialPublish.validation.tiktokDisclosure'),
        };
      }
    }

    if (provider === SocialProvider.YouTube) {
      const youtubeMetadata =
        activePublishedAccount.metadata as YouTubeMetadata;

      if (!youtubeMetadata?.privacyStatus) {
        return {
          valid: false,
          message: t('videoEditor.socialPublish.validation.youtubePrivacy'),
        };
      }
    }

    return {
      valid: true,
      message: '',
    };
  };

  const metadataValidity = isMetadataValid();

  const renderSelectedAccountInfo = () => {
    if (!selectedAccount) return null;

    return (
      <Flex align="center" gap={12} style={{ margin: 0 }}>
        <Avatar src={selectedAccount.avatarUrl} size="small" />
        <Typography.Text strong>{selectedAccount.title}</Typography.Text>
      </Flex>
    );
  };

  const getSocialTypes = (): SocialProvider[] => {
    if (!publishedAccounts || publishedAccounts.length === 0) {
      return [
        SocialProvider.TikTok,
        SocialProvider.YouTube,
        SocialProvider.Instagram,
        SocialProvider.Facebook,
      ];
    }

    const availablePlatforms = [
      SocialProvider.TikTok,
      SocialProvider.YouTube,
      SocialProvider.Instagram,
      SocialProvider.Facebook,
    ];

    const existingPlatforms = publishedAccounts.map(
      (account) => account.socialPlatform
    );

    return availablePlatforms.filter(
      (platform) => !existingPlatforms.includes(platform)
    );
  };

  const availableSocialTypes = getSocialTypes();

  if (!publishedAccounts || publishedAccounts.length === 0) {
    return (
      <SocialAccountSelect
        value={activePublishedAccount?.socialAccountId}
        onChange={handleAccountSelect}
        disabled={availableSocialTypes.length === 0}
        socialTypes={availableSocialTypes}
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Segmented
        block
        value={activeTab}
        onChange={handleTabChange}
        options={getOptions()}
      />
      {renderSelectedAccountInfo()}
      {activeTab === 'add' ? (
        <SocialAccountSelect
          value={activePublishedAccount?.socialAccountId}
          onChange={handleAccountSelect}
          disabled={availableSocialTypes.length === 0}
          socialTypes={availableSocialTypes}
        />
      ) : null}
      {getSocialProviderAdditionalInfo()}
      {!metadataValidity.valid ? (
        <Typography.Text type="secondary">
          {metadataValidity.message}
        </Typography.Text>
      ) : null}
      {getSocialAgreement()}
    </Space>
  );
};

export default observer(SocialAccountTabs);
